// ABOUTME: An attachment picked for a message, over its field: a picture shows itself, a video or a file its name and
// ABOUTME: size — with its upload's state, and how one handed back by a refused message is shown again.
import { AssetKind } from "@/graphql/main/gql/graphql"
import { serverErrorCode } from "@/graphql/client/errorMessage"
import { FileRefusal } from "@/model/assets/fileTypes"
import { UploadFailure, type UploadAttempt, type UploadProgress } from "@/model/assets/storageUpload"
import { imageUrl } from "@/model/imageUrl"
import type { AttachedAsset, AttachedFile, AttachedPicture } from "./attachedAsset"

interface Picked {
  key: string
  status: "uploading" | "uploaded" | "failed"
  error: unknown
  /** Kept for another try; null for one handed back by a refused message, which is uploaded already. */
  file: File | null
}

/** A picture over the field shows itself: the picked file while it uploads, or the uploaded picture's path. */
export interface PickedPicture extends Picked {
  kind: AssetKind.Image
  preview: string
  asset: AttachedPicture | null
}

/** A video or a file over the field shows its name and size — and, while it uploads, how far it got. */
export interface PickedFile extends Picked {
  kind: AssetKind.Video | AssetKind.File
  name: string
  sizeBytes: number
  contentType: string
  asset: AttachedFile | null
  /** Where its upload is; null once settled, and for one refused before it or handed back. */
  progress: UploadProgress | null
  /** The attempt the server knows its upload by, for another try (↻); null when there was no upload. */
  attempt: UploadAttempt | null
}

export type PickedAttachment = PickedPicture | PickedFile

/**
 * Worth another try: no answer came, the service that prepares pictures could not take it just then, or a file's
 * upload broke off. The rest — not an image, a type not taken, too large, the day's budget spent — would be refused
 * the same way again.
 */
export const isRetryableUpload = (error: unknown): boolean => {
  if (error instanceof UploadFailure) return error.isRetryable
  if (error instanceof FileRefusal) return false
  const code = serverErrorCode(error)
  return code === null || code === "ErrAssetProcessingUnavailable"
}

/** A refused message's attachments, back in the field: uploaded already, nothing to send again. */
export const handedBack = (asset: AttachedAsset): PickedAttachment => {
  const picked = { key: asset.id, status: "uploaded", error: null, file: null } as const
  return asset.kind === AssetKind.Image
    ? { ...picked, kind: asset.kind, preview: imageUrl(asset.path), asset }
    : {
        ...picked,
        kind: asset.kind,
        name: asset.name,
        sizeBytes: asset.sizeBytes,
        contentType: asset.contentType,
        asset,
        progress: null,
        attempt: null,
      }
}
